import type { JwtPayload } from "../api/types";
import {
  normalizeNavigationPayload,
  type NavigationPayload,
} from "../api/navigation";

/**
 * Org-assignable permission catalog (kept in sync with user-service org-roles.ts).
 * Used to expand JWT `org_system_key: "admin"` without embedding the full list in the cookie.
 */
export const ORG_ASSIGNABLE_PERMISSION_NAMES = [
  "org:read",
  "org:manage",
  "org:settings:read",
  "org:settings:write",
  "org:members:read",
  "org:members:invite",
  "org:members:remove",
  "org:members:roles",
  "org:roles:read",
  "org:roles:write",
  "org:reports:read",
  "org:assignments:read",
  "org:assignments:write",
  "product:read",
  "product:create",
  "product:update",
  "product:delete",
  "device:read",
  "device:create",
  "device:update",
  "device:delete",
  "entity:read",
  "entity:create",
  "entity:update",
  "entity:delete",
  "component:read",
  "component:create",
  "component:update",
  "component:delete",
  "certification:read",
  "certification:enroll",
  "certification:take-quiz",
  "certification:view",
  "certification:manage",
] as const;

function effectivePermissionList(payload: JwtPayload): string[] {
  const merged = [
    ...(payload.permissions ?? []),
    ...(payload.org_permissions ?? []),
  ];
  if (payload.org_system_key === "admin") {
    merged.push(...ORG_ASSIGNABLE_PERMISSION_NAMES);
  }
  return [...new Set(merged)];
}

/**
 * Synchronous permission check against JWT payload (matches user-service wildcard rules).
 */
export function hasPermissionInPayload(
  payload: JwtPayload | null | undefined,
  permission: string,
): boolean {
  if (!payload) {
    return false;
  }

  const userPermissions = effectivePermissionList(payload);

  if (!userPermissions.length) {
    return false;
  }

  if (userPermissions.includes(permission)) {
    return true;
  }

  const wildcardPermissions = userPermissions.filter((p) => p.endsWith(":*"));
  for (const wildcard of wildcardPermissions) {
    const base = wildcard.slice(0, -2);
    if (permission.startsWith(`${base}:`)) {
      return true;
    }
  }

  const parts = permission.split(":");
  if (parts.length === 3) {
    const basePermission = `${parts[0]}:${parts[1]}`;
    if (userPermissions.includes(basePermission)) {
      return true;
    }
  }

  return false;
}

export function hasRoleInPayload(
  payload: JwtPayload | null | undefined,
  role: string,
): boolean {
  return Boolean(payload?.roles?.includes(role));
}

export function syncNavigationCache(navigation: NavigationPayload | undefined): void {
  if (typeof sessionStorage === "undefined") return;
  try {
    if (navigation?.modules?.length) {
      sessionStorage.setItem(
        "lion_pro_navigation",
        JSON.stringify(navigation),
      );
    } else {
      sessionStorage.removeItem("lion_pro_navigation");
    }
  } catch {
    /* ignore */
  }
}

/** @deprecated Use syncNavigationCache */
export function syncEnabledModulesCache(enabledModules: string[] | undefined): void {
  if (!enabledModules?.length) {
    sessionStorage.removeItem("lion_pro_navigation");
    return;
  }
  syncNavigationCache({
    modules: enabledModules.map((slug) => ({
      slug,
      label: slug,
      description: null,
      icon: null,
      views: [],
    })),
  });
}

export function readCachedNavigation(): NavigationPayload | null {
  if (typeof sessionStorage === "undefined") return null;
  try {
    const raw = sessionStorage.getItem("lion_pro_navigation");
    if (!raw) return null;
    return normalizeNavigationPayload(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function hasModuleAccess(moduleSlug: string): boolean {
  const navigation = readCachedNavigation();
  if (!navigation?.modules.length) {
    return false;
  }

  const aliases =
    moduleSlug === "workspace"
      ? ["workspace", "administration"]
      : [moduleSlug];

  return navigation.modules.some((m) => aliases.includes(m.slug));
}
